import { Fragment, useState } from 'react';
import type { ChannelAccess } from '../../shared/discord-perms';
import type { DiscordChannel, DiscordThread } from '../../shared/types';
import { fmtAgo, useApi } from '../lib/hooks';
import { useMe } from '../lib/me';
import { api } from '../lib/api';
import { Icon } from '../components/Icon';
import { Alert, Badge, Button, ErrorBox, Field, Loading, Panel, Stat, Toggle, useAction } from '../components/ui';

const TYPE: Record<number, string> = { 0: 'text', 2: 'voice', 4: 'category', 5: 'announcement', 13: 'stage', 15: 'forum', 16: 'media' };
const FORUM = new Set([15, 16]);
type ChannelRow = DiscordChannel & { access: ChannelAccess | null };
interface Names { roles: { id: string; name: string; color: number; managed: boolean; botId: string | null; isEveryone: boolean }[]; members: Record<string, string> }
const hex = (n: number) => (n ? `#${n.toString(16).padStart(6, '0')}` : '#99aab5');

export function DiscordPage() {
  const { data, error, loading, reload } = useApi<any>('/api/admin/discord');
  const ch = useApi<ChannelRow[]>('/api/admin/discord/channels');
  const names = useApi<Names>('/api/admin/discord/names');
  const [open, setOpen] = useState<string | null>(null);
  const [privateOnly, setPrivateOnly] = useState(false);
  const th = useApi<DiscordThread[]>('/api/admin/discord/threads');
  const { can } = useMe();
  const manage = can('admin.discord.manage');
  const { busy, run } = useAction();
  const [guildId, setGuildId] = useState<string | null>(null);
  const [intervals, setIntervals] = useState<any>(null);

  if (loading && !data) return <Loading />;
  if (error) return <ErrorBox error={error} retry={reload} />;
  if (!data) return null;
  const gid = guildId ?? data.guildId;
  const iv = intervals ?? data.settings;
  const channels = ch.data ?? [];
  const categories = new Map(channels.filter((c) => c.type === 4).map((c) => [c.id, c.name]));
  const readable = channels.filter((c) => c.type !== 4 && (!privateOnly || (c.access && !c.access.everyone)));
  const roleOf = (id: string) => names.data?.roles.find((r) => r.id === id);

  const setChannel = (c: DiscordChannel, patch: Partial<DiscordChannel>) =>
    run(`ch-${c.id}`, async () => { await api.patch(`/api/admin/discord/channels/${c.id}`, patch); await ch.reload(); await th.reload(); });

  return (
    <div className="stack">
      {channels.some((c) => c.feedEnabled && c.feedError && !c.deleted) && (
        <Alert kind="danger">
          <b>Some channels can't be read:</b>{' '}
          {channels.filter((c) => c.feedEnabled && c.feedError && !c.deleted).map((c) => `#${c.name}`).join(', ')}. Their posts stop updating on the dashboard (the other channels keep working). The reason is shown next to each channel below.
        </Alert>
      )}
      {data.mode === 'fake' && <Alert kind="warn">Development mode: connected to a <b>fake Discord server</b>. Deploy with the bot token and DISCORD_MODE=live to use your real (or test) server.</Alert>}
      <div className="stats">
        <Stat icon="discord" label="Bot" value={data.bot.ok ? <span className="ok-text">Connected</span> : <span className="bad-text">Offline</span>} hint={data.bot.ok ? `as ${data.bot.username}` : data.bot.error} />
        <Stat icon="users" label="Members" value={data.counts.members} hint={`last sync ${fmtAgo(data.jobs.sync?.lastRunAt)}`} />
        <Stat icon="roles" label="Roles" value={data.counts.roles} />
        <Stat icon="megaphone" label="Feed channels" value={`${data.counts.feeds} / ${data.counts.channels}`} hint={`${data.counts.threads ?? 0} threads · last feed sync ${fmtAgo(data.jobs.feeds?.lastRunAt)}`} />
      </div>

      <div className="grid-2">
        <Panel title="Server connection" icon="discord">
          <Field label="Discord server (guild) ID" hint="Right-click the server in Discord → Copy Server ID (Developer Mode on). The bot must already be in that server.">
            <input value={gid} disabled={!manage} onChange={(e) => setGuildId(e.target.value.trim())} className="mono" />
          </Field>
          <p className="small">Connected to: <b>{data.bot.guildName ?? data.guildName ?? '—'}</b></p>
          {gid !== data.guildId && <Alert kind="warn">Switching servers changes every Discord role ID. After saving, run a sync, then use <b>Clearance Controls → Remap roles</b> to move your policies onto the new server's roles.</Alert>}
          <div className="panel-foot">
            {data.inviteUrl && <a className="btn btn-ghost" href={data.inviteUrl} target="_blank" rel="noreferrer"><Icon name="external" size={16} /> Bot invite link</a>}
            {manage && <Button variant="primary" disabled={gid === data.guildId} busy={busy === 'guild'} onClick={() => run('guild', async () => { await api.put('/api/admin/discord/settings', { guildId: gid }); setGuildId(null); await reload(); }, 'Server saved. Run a sync next.')}>Save server</Button>}
          </div>
        </Panel>

        <Panel title="Sync" icon="refresh">
          <div className="form-row">
            <Field label="Role & member sync (minutes)"><input type="number" min={5} value={iv.syncIntervalMinutes} disabled={!manage} onChange={(e) => setIntervals({ ...iv, syncIntervalMinutes: Number(e.target.value) })} /></Field>
            <Field label="Feed sync (minutes)"><input type="number" min={5} value={iv.feedIntervalMinutes} disabled={!manage} onChange={(e) => setIntervals({ ...iv, feedIntervalMinutes: Number(e.target.value) })} /></Field>
            <Field label="Messages kept per channel"><input type="number" min={10} value={iv.feedRetentionPerChannel} disabled={!manage} onChange={(e) => setIntervals({ ...iv, feedRetentionPerChannel: Number(e.target.value) })} /></Field>
          </div>
          <p className="muted small">Members also get their roles refreshed instantly every time they sign in.</p>
          {manage && (
            <div className="panel-foot">
              <Button disabled={!intervals} busy={busy === 'iv'} onClick={() => run('iv', async () => { await api.put('/api/admin/discord/settings', intervals); setIntervals(null); await reload(); }, 'Sync settings saved')}>Save intervals</Button>
              <Button icon="megaphone" busy={busy === 'feeds'} onClick={() => run('feeds', async () => { const r = await api.post<{ failed?: { name: string }[] }>('/api/admin/discord/sync', { what: 'feeds' }); await reload(); await ch.reload(); await th.reload(); if (r.failed?.length) throw new Error(`Feeds synced, but these couldn't be read: ${r.failed.map((f) => `#${f.name}`).join(', ')}. See the reason next to each channel.`); return r; }, 'Feeds synced')}>Sync feeds</Button>
              <Button variant="primary" icon="refresh" busy={busy === 'full'} onClick={() => run('full', async () => { await api.post('/api/admin/discord/sync', { what: 'full' }); await reload(); await ch.reload(); }, 'Roles, members, channels and events synced')}>Full sync now</Button>
            </div>
          )}
        </Panel>
      </div>

      <Panel title="Channels the bot can see" icon="megaphone" pad={false}
        actions={<label className="check-row small"><input type="checkbox" checked={privateOnly} onChange={(e) => setPrivateOnly(e.target.checked)} /> Only restricted channels</label>}>
        {readable.length === 0 ? <p className="muted pad">No channels yet. Run a full sync.</p> : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Channel</th><th>Type</th><th>Who can see it</th><th>Read messages</th><th title="Also read the threads in this channel. Forums are always read through their posts.">Threads</th><th>Show on home</th><th title="Add the channel's widget to the home page of members who haven't customised theirs">Default widget</th></tr></thead>
              <tbody>
                {readable.map((c) => {
                  const canRead = c.type === 0 || c.type === 5 || FORUM.has(c.type);
                  const threadCount = (th.data ?? []).filter((t) => t.parentId === c.id).length;
                  const isOpen = open === c.id;
                  return (
                    <Fragment key={c.id}>
                    <tr className={isOpen ? 'row-open' : ''}>
                      <td><span className="mono">#{c.name}</span>{c.parentId && <span className="muted small"> in {categories.get(c.parentId)}</span>}{c.feedEnabled && c.feedError && <div className="feed-error small"><Icon name="warn" size={11} /> {c.feedError}</div>}</td>
                      <td><Badge tone={c.type === 5 ? 'gold' : FORUM.has(c.type) ? 'blue' : 'neutral'}>{TYPE[c.type] ?? c.type}</Badge></td>
                      <td>
                        {!c.access ? <span className="muted small">run a full sync</span> : (
                          <button className="access-sum" onClick={() => setOpen(isOpen ? null : c.id)} aria-expanded={isOpen} title="Show who can access this channel">
                            {c.access.everyone ? <Badge tone="green">Everyone</Badge> : <Badge tone="amber"><Icon name="lock" size={11} /> {c.access.roles.length} role{c.access.roles.length === 1 ? '' : 's'}</Badge>}
                            {(c.access.denied.length > 0 || c.access.members.length > 0) && <span className="muted small">+ overrides</span>}
                            <Icon name="down" size={12} className={isOpen ? 'flip' : ''} />
                          </button>
                        )}
                      </td>
                      <td>{canRead ? <Toggle checked={c.feedEnabled} disabled={!manage || busy === `ch-${c.id}`} onChange={(v) => setChannel(c, { feedEnabled: v })} label={`Read #${c.name}`} /> : <span className="muted small">not readable</span>}</td>
                      <td className="nowrap">
                        {!canRead ? null : FORUM.has(c.type)
                          ? <span className="muted small">{c.feedEnabled ? `posts (${threadCount})` : 'posts'}</span>
                          : <><Toggle checked={!!c.threadsEnabled} disabled={!manage || !c.feedEnabled || busy === `ch-${c.id}`} onChange={(v) => setChannel(c, { threadsEnabled: v })} label={`Read threads in #${c.name}`} />{c.threadsEnabled && <span className="muted small"> {threadCount}</span>}</>}
                      </td>
                      <td>{canRead ? <Toggle checked={c.showOnHome} disabled={!manage || !c.feedEnabled || busy === `ch-${c.id}`} onChange={(v) => setChannel(c, { showOnHome: v })} label={`Show #${c.name} on home`} /> : null}</td>
                      <td>{canRead ? <Toggle checked={!!c.homeDefault} disabled={!manage || !c.showOnHome || busy === `ch-${c.id}`} onChange={(v) => setChannel(c, { homeDefault: v })} label={`Add #${c.name} to new members' home pages`} /> : null}</td>
                    </tr>
                    {isOpen && c.access && (
                      <tr className="access-row"><td colSpan={7}><AccessDetail access={c.access} names={names.data} botId={data.bot.id} roleOf={roleOf} /></td></tr>
                    )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="muted small pad">The bot only sees channels where it has View Channel and Read Message History in Discord. "Threads" also reads the public threads in a channel; forums are read through their posts (each post is a thread). Private threads are never read. Enabled channels are stored for tools to use. "Show on home" offers the channel as its own home page widget; members add it from Customize home → Add widget. "Default widget" also puts it on the home page of members who haven't customised theirs (new members, for example).</p>
      </Panel>

      <div className="grid-2">
        <ThreadsPanel threads={th.data ?? []} channels={channels} />
        <CapabilitiesPanel caps={data.capabilities} inviteUrl={data.inviteUrl} />
      </div>
    </div>
  );
}

/** Who can access a channel, laid out like Discord's own list. */
function AccessDetail({ access, names, botId, roleOf }: { access: ChannelAccess; names: Names | null; botId?: string; roleOf: (id: string) => Names['roles'][number] | undefined }) {
  const label = (id: string) => {
    const r = roleOf(id);
    return (
      <span className="access-role">
        <i style={{ background: hex(r?.color ?? 0) }} />{r?.name ?? id}
        {r?.botId && <Badge tone={r.botId === botId ? 'blue' : 'neutral'}>{r.botId === botId ? 'this dashboard’s bot' : 'another bot'}</Badge>}
      </span>
    );
  };
  const REASON = { administrator: 'Administrator', allowed: 'Allowed on this channel', server: 'Server permission' } as const;
  return (
    <div className="access-detail">
      <div>
        <h5>{access.everyone ? 'Everyone can see this channel' : 'Who can access this channel'}</h5>
        {access.roles.length === 0 ? <p className="muted small">{access.everyone ? 'No roles have their own settings here.' : 'Only Administrators can see it.'}</p> : (
          <ul>
            {access.roles.map((r) => (
              <li key={r.id}>{label(r.id)}<span className="muted small">{REASON[r.reason]}{r.canPost ? '' : ' · read only'}</span></li>
            ))}
          </ul>
        )}
      </div>
      {access.denied.length > 0 && (
        <div>
          <h5>Blocked on this channel</h5>
          <ul>{access.denied.map((id) => <li key={id}>{label(id)}<span className="muted small">View Channel denied</span></li>)}</ul>
        </div>
      )}
      {access.members.length > 0 && (
        <div>
          <h5>Members set individually</h5>
          <ul>{access.members.map((m) => <li key={m.id}><span className="access-role">{names?.members[m.id] ?? `Discord user ${m.id}`}</span><span className="muted small">{m.allowed ? 'Allowed' : 'Blocked'}</span></li>)}</ul>
        </div>
      )}
      <p className="muted small">From Discord’s role permissions and this channel’s own permission settings, as of the last full sync. Each role is checked on its own, like Discord’s “Who can access this channel?” list.</p>
    </div>
  );
}

function ThreadsPanel({ threads, channels }: { threads: DiscordThread[]; channels: DiscordChannel[] }) {
  const [all, setAll] = useState(false);
  const names = new Map(channels.map((c) => [c.id, c.name]));
  const shown = all ? threads : threads.slice(0, 12);
  return (
    <Panel title="Threads and forum posts" icon="list" pad={false}>
      {threads.length === 0 ? <p className="muted pad">No threads read yet. Switch on Threads for a channel (or read a forum), then sync feeds.</p> : (
        <div className="table-wrap">
          <table className="table thread-list">
            <thead><tr><th>Thread</th><th>In</th><th>Status</th><th className="right">Posts</th><th>Active</th></tr></thead>
            <tbody>
              {shown.map((t) => (
                <tr key={t.id}>
                  <td><strong>{t.name}</strong></td>
                  <td className="mono small">#{names.get(t.parentId) ?? '?'}</td>
                  <td>{t.locked ? <Badge tone="red">locked</Badge> : t.archived ? <Badge>archived</Badge> : <Badge tone="green">active</Badge>}</td>
                  <td className="right">{t.messageCount}</td>
                  <td className="nowrap muted small">{t.activeAt ? fmtAgo(t.activeAt) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {threads.length > 12 && <div className="pad"><button className="link-btn small" onClick={() => setAll(!all)}>{all ? 'Show fewer' : `Show all ${threads.length}`}</button></div>}
      <p className="muted small pad">Active threads are checked every feed sync and only read when they have new posts. Archived threads are read once, when Threads is switched on. Up to 40 threads with new posts are read per sync.</p>
    </Panel>
  );
}

type Cap = { id: string; label: string; used: string; has: boolean; now: boolean; admin: boolean };
function CapabilitiesPanel({ caps, inviteUrl }: { caps: Cap[] | null; inviteUrl: string | null }) {
  return (
    <Panel title="What the bot is allowed to do" icon="shield">
      {!caps ? <p className="muted small">Connect the bot to a server to check its permissions.</p> : (<>
        {caps[0]?.admin && <Alert kind="warn">The bot has <b>Administrator</b> in Discord. It works, but giving it only the permissions below is safer.</Alert>}
        <ul className="bot-caps">
          {caps.map((c) => (
            <li key={c.id}>
              <span className={c.has ? 'ok-text' : c.now ? 'bad-text' : 'muted'} aria-hidden="true">{c.has ? '✓' : c.now ? '✕' : '–'}</span>
              <div><strong>{c.label}</strong><small>{c.used}</small></div>
              <Badge tone={c.now ? (c.has ? 'green' : 'red') : 'neutral'}>{c.now ? (c.has ? 'in use' : 'needed') : c.has ? 'granted, unused' : 'not needed yet'}</Badge>
            </li>
          ))}
        </ul>
        <p className="muted small">Checked from the bot's server roles; a channel's own permission settings can still hide it from the bot. The dashboard posts and edits only its own event posts (Events tool) and creates their threads and Discord events; it never deletes other people’s messages.</p>
      </>)}
      {inviteUrl && <div className="panel-foot"><a className="btn btn-ghost" href={inviteUrl} target="_blank" rel="noreferrer"><Icon name="external" size={16} /> Bot invite link (permissions for feeds, events and attendance)</a></div>}
    </Panel>
  );
}
